import React, { useContext, useRef, MutableRefObject } from 'react';
import { UploadImage } from '../../assets/icons/ImageEditTool';
import { StyledTool, ToolButton } from '../Styled';
import { StyledWordContext } from '../../contexts/styledWordContext';
import { ImageDimensions, ImageSide } from '../../types';
import { RemoveRedEyeIcon, SetRedEyeIcon, setImage, removeImage } from '../../assets/icons/RedEye';
import { ButtonColor, ButtonVariant } from 'react-magma-dom';
import { getHiddenImageId } from '../../utils';

interface Image {
  id: string;
  default_uuid: string | undefined;
  uuid: string;
  defaultImageDimensions: ImageDimensions;
  imageKey?: string;
  side?: ImageSide;
  editMode: boolean;
  isHidden?: boolean;
  isUploadAvailable?: boolean;
  isEyeIconHidden?: boolean;
  uploadImage: (
    id: string,
    default_uuid: string | undefined,
    sectionId: string,
    defaultImageDimensions: ImageDimensions,
    side?: ImageSide
  ) => void;
}

export const EditImageTool = (props: Image): JSX.Element => {
  const {
    id,
    uuid,
    default_uuid,
    defaultImageDimensions,
    side,
    editMode,
    isHidden,
    isUploadAvailable,
    isEyeIconHidden,
    uploadImage,
  } = props;
  const fontToolRef = useRef() as MutableRefObject<HTMLDivElement>;
  const { styledWordState, handleImage, saveImageHiddenUpdates } = useContext(StyledWordContext);
  const hiddenImageId = getHiddenImageId(default_uuid, uuid);
  const isImageHidden = isHidden !== undefined ? isHidden : styledWordState?.images?.includes(hiddenImageId);
  const uploadButtonIsEnabled = isUploadAvailable === undefined ? true : isUploadAvailable;

  const handleToolClick = () => {
    if (styledWordState.images.includes(hiddenImageId)) {
      const restImages = styledWordState.images.filter((image: string | number) => {
        return image !== hiddenImageId;
      });

      saveImageHiddenUpdates(styledWordState.images, restImages);
    } else {
      saveImageHiddenUpdates(styledWordState.images, [...styledWordState.images, hiddenImageId]);
    }
    handleImage(hiddenImageId);
  };

  const getIcon = (): JSX.Element => (isImageHidden ? RemoveRedEyeIcon(removeImage) : SetRedEyeIcon(setImage));

  const getLabel = (): string => (isImageHidden ? 'Show image' : 'Hide image');

  return (
    <>
      {editMode && styledWordState.imageTool.isVisible ? (
        <StyledTool data-testid="image-tool" ref={fontToolRef}>
          <ToolButton
            disabled={!uploadButtonIsEnabled}
            variant={ButtonVariant.link}
            color={ButtonColor.secondary}
            tabIndex={0}
            onClick={() => {
              uploadImage(uuid, default_uuid, id, defaultImageDimensions, side);
            }}
            testId="image-tool-upload"
            aria-label="Upload image"
            icon={<UploadImage />}
            margin={isEyeIconHidden ? '1px' : ''}
          />
          {!isEyeIconHidden && (
            <ToolButton
              variant={ButtonVariant.link}
              color={ButtonColor.secondary}
              tabIndex={0}
              testId="image-tool-visibility"
              aria-label={getLabel()}
              icon={getIcon()}
              onClick={handleToolClick}
            />
          )}
        </StyledTool>
      ) : (
        <></>
      )}
    </>
  );
};
